import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {catchError, shareReplay, tap} from 'rxjs/operators';
import {ClientService} from '../auth/client.service';
import {environment} from '../../../environments/environment';

export type LlmProvider = 'openai' | 'anthropic' | 'gemini';

export interface LlmProviderModels {
  provider: LlmProvider;
  configured: boolean;
  cached: boolean;
  models: string[];
  error?: string;
}

export interface LlmModelsResponse {
  retrievedAt: string;
  providers: LlmProviderModels[];
}

@Injectable({providedIn: 'root'})
export class LlmModelsService {
  private http = inject(HttpClient);
  private clientService = inject(ClientService);

  private static readonly SuccessCacheTtlMs = 60 * 60 * 1000;
  private static readonly FailureCacheTtlMs = 2 * 60 * 1000;

  private cachedByClientId = new Map<string, { expiresAt: number; obs: Observable<LlmModelsResponse> }>();

  getModels(refresh = false): Observable<LlmModelsResponse> {
    const clientId = this.clientService.client?.id;
    return this.getModelsForClient(clientId ?? '', refresh);
  }

  getModelsForClient(clientId: string, refresh = false): Observable<LlmModelsResponse> {
    if (!clientId) return of({retrievedAt: new Date().toISOString(), providers: []});

    if (refresh) return this.fetchAndCache(clientId, true);

    const now = Date.now();
    const existing = this.cachedByClientId.get(clientId);
    if (existing && existing.expiresAt > now) return existing.obs;
    if (existing) this.cachedByClientId.delete(clientId);

    return this.fetchAndCache(clientId, false);
  }

  private fetch(clientId: string, refresh: boolean): Observable<LlmModelsResponse> {
    const url = `${environment.aiBaseServerUrl}${clientId}/llm/models${refresh ? '?refresh=true' : ''}`;
    return this.http.get<LlmModelsResponse>(url).pipe(
      catchError(() => of({retrievedAt: new Date().toISOString(), providers: []})),
    );
  }

  private fetchAndCache(clientId: string, refresh: boolean): Observable<LlmModelsResponse> {
    const entry: { expiresAt: number; obs: Observable<LlmModelsResponse> } = {
      expiresAt: Date.now() + LlmModelsService.FailureCacheTtlMs,
      obs: of({retrievedAt: new Date().toISOString(), providers: []}),
    };

    entry.obs = this.fetch(clientId, refresh).pipe(
      tap((resp) => {
        entry.expiresAt = Date.now() + this.getTtlMs(resp);
      }),
      shareReplay(1),
    );

    this.cachedByClientId.set(clientId, entry);
    return entry.obs;
  }

  private getTtlMs(resp: LlmModelsResponse | null | undefined): number {
    const providers = resp?.providers ?? [];
    const anyModels = providers.some((p) => (p.models ?? []).length > 0);
    return anyModels ? LlmModelsService.SuccessCacheTtlMs : LlmModelsService.FailureCacheTtlMs;
  }
}
